import { Box } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import {
  ChartInstance,
  ContentWithLegend,
  ContentWithLegendProps,
  YAxisLabel,
  YAxisLabelProps,
} from '../../../components';
import { ContentDimensions } from '../../../core';
import { TimeChartBase, TimeChartBaseProps } from './TimeChartBase';
import { TimeSeriesChartExtendedYAxisOptions } from './time-series-chart-model';

export type TimeSeriesChartPanelContentProps = ContentDimensions &
  Pick<
    TimeChartBaseProps,
    | 'dataset'
    | 'events'
    | 'grid'
    | 'isStacked'
    | 'onDataZoom'
    | 'onClick'
    | 'seriesMapping'
    | 'syncGroup'
    | 'timeScale'
    | 'unit'
    | 'xAxisMinMs'
    | 'xAxisMaxMs'
    | 'xAxisRangeMs'
  > &
  Pick<
    ContentWithLegendProps,
    | 'legendProps'
    | 'legendSize'
    | 'quickFilterProps'
    | 'enableConsistentLegendSize'
    | 'toggleModeProps'
    | 'layoutStrategy'
    | 'collapsibleLegendProps'
    | 'groupByProps'
  > & {
    chartRef: React.RefObject<ChartInstance>;
    yAxis: TimeSeriesChartExtendedYAxisOptions;
    yAxisRight?: TimeSeriesChartExtendedYAxisOptions;
    yAxisLabel?: YAxisLabelProps['name'];
    yAxisAltLabel?: YAxisLabelProps['name'];
    panelKey?: string;
  };

/**
 * This component is exposed to consumers via the `TimeSeriesChart.PanelContent`. More detailed, consumer-facing
 * jsdoc is located where that mapping is defined in `TimeSeriesChart` to ensure it is available for consumers.
 */
export function TimeSeriesChartPanelContent({
  chartRef,
  dataset,
  events,
  grid,
  height,
  isStacked,
  legendProps,
  legendSize,
  onDataZoom,
  onClick,
  seriesMapping,
  syncGroup,
  timeScale,
  unit,
  width,
  yAxis,
  yAxisLabel,
  yAxisRight,
  yAxisAltLabel,
  quickFilterProps,
  toggleModeProps,
  enableConsistentLegendSize,
  layoutStrategy,
  panelKey,
  collapsibleLegendProps,
  groupByProps,
  xAxisMinMs,
  xAxisMaxMs,
  xAxisRangeMs,
}: TimeSeriesChartPanelContentProps) {
  const yAxes = useMemo(() => [yAxis, yAxisRight ?? { show: false, type: 'value' }], [yAxis, yAxisRight]);

  return (
    <ContentWithLegend
      width={width}
      height={height}
      spacing={8}
      // Making this small enough that the medium size doesn't get
      // responsive-handling-ed away when in the panel options editor.
      minChildrenHeight={50}
      legendSize={legendSize}
      legendProps={legendProps}
      totalLegendItems={dataset.length}
      quickFilterProps={quickFilterProps}
      toggleModeProps={toggleModeProps}
      enableConsistentLegendSize={enableConsistentLegendSize}
      layoutStrategy={layoutStrategy}
      collapsibleLegendProps={collapsibleLegendProps}
      groupByProps={groupByProps}
    >
      {({ height, width }) => {
        return (
          <Box sx={{ height, width }} position="relative" id={panelKey}>
            {yAxis && yAxis.show && yAxisLabel && <YAxisLabel name={yAxisLabel} height={height} position="left" />}
            <TimeChartBase
              ref={chartRef}
              width={width}
              height={height}
              dataset={dataset}
              seriesMapping={seriesMapping}
              timeScale={timeScale}
              yAxes={yAxes}
              unit={unit}
              grid={grid}
              isStacked={isStacked}
              syncGroup={syncGroup}
              onDataZoom={onDataZoom}
              events={events}
              onClick={onClick}
              xAxisMinMs={xAxisMinMs}
              xAxisMaxMs={xAxisMaxMs}
              xAxisRangeMs={xAxisRangeMs}
            />
            {yAxisRight?.show === true && yAxisAltLabel !== undefined && (
              <YAxisLabel name={yAxisAltLabel} height={height} position="right" />
            )}
          </Box>
        );
      }}
    </ContentWithLegend>
  );
}
